/**
 * Dashboard-level filter wrapper.
 *
 * Custom dashboard-level filters are applied by WRAPPING each component's
 * SQL with an additional WHERE clause, rather than splicing into the
 * existing WHERE. This is robust against CTEs, UNIONs, subqueries, GROUP
 * BY / HAVING and any other shape the inner SQL might take:
 *
 *   SELECT * FROM ( <original_sql> ) _df_wrap
 *   WHERE "COL" IN ('a','b') AND "OTHER" BETWEEN 1 AND 10
 *
 * A filter is AUTO-SKIPPED for a card whose result-set columns do not
 * include the filter's column (so a "REGION" filter only narrows cards
 * that actually expose REGION).
 *
 * Filter definitions match the backend schema:
 *   { id, label, column, operator, value_source, default_values,
 *     multi_select, applies_to }
 */

const _NUMERIC_RE = /^-?\d+(\.\d+)?$/;

/** Quote an identifier safely (Snowflake-style double quotes). */
function quoteIdent(name) {
  const safe = String(name || '').replace(/"/g, '""');
  return `"${safe}"`;
}

/**
 * Quote a value for inclusion in a SQL literal. Strings are wrapped in
 * single quotes (with `'` doubled); numeric-looking values are passed
 * through. Null-like markers become `NULL`.
 */
function quoteValue(v) {
  if (v == null || v === '') return 'NULL';
  if (v === '(null)' || v === 'NULL') return 'NULL';
  const s = String(v);
  if (_NUMERIC_RE.test(s)) return s;
  return `'${s.replace(/'/g, "''")}'`;
}

/**
 * Strip trailing whitespace + semicolons so we can safely embed the
 * original SQL inside a subquery.
 */
function normalizeInner(sql) {
  return String(sql || '').replace(/;\s*$/g, '').trim();
}

/**
 * Build a single predicate fragment for one active filter.
 * Returns null if the filter does not apply (no values, etc.).
 */
function buildPredicate(def, rawValues) {
  if (!def || !def.column) return null;
  const op = (def.operator || 'IN').toUpperCase();
  const col = quoteIdent(def.column);

  // Null-check operators carry no value — they're "active" iff any
  // truthy entry is present in the values array (the bar renders this
  // as a Switch that toggles `['1']` / `[]`).
  if (op === 'IS NULL' || op === 'IS NOT NULL') {
    const active = Array.isArray(rawValues) && rawValues.some((v) => v !== undefined && v !== '' && v !== false);
    if (!active) return null;
    return `${col} ${op}`;
  }

  const values = Array.isArray(rawValues) ? rawValues.filter((v) => v !== undefined && v !== '') : [];
  if (values.length === 0) return null;

  switch (op) {
    case 'IN':
    case 'NOT IN': {
      const list = values.map(quoteValue).join(', ');
      return `${col} ${op} (${list})`;
    }
    case '=':
    case '!=':
    case '>':
    case '>=':
    case '<':
    case '<=':
      return `${col} ${op} ${quoteValue(values[0])}`;
    case 'LIKE':
    case 'ILIKE':
    case 'NOT LIKE':
    case 'NOT ILIKE':
      // Pattern is taken verbatim from the user's input — they're
      // expected to include `%` wildcards as needed.
      return `${col} ${op} ${quoteValue(values[0])}`;
    case 'BETWEEN': {
      if (values.length < 2) return null;
      return `${col} BETWEEN ${quoteValue(values[0])} AND ${quoteValue(values[1])}`;
    }
    default:
      return null;
  }
}

/**
 * Decide whether a filter definition applies to a card with the given
 * result-set columns.
 *
 *   - `applies_to: "all"` (default) → applies iff the column is present
 *     in the card's output (auto-skip if not).
 *   - `applies_to: [item_id, ...]`  → applies iff the card's id is in the
 *     list AND the column is present.
 *
 * Column matching is case-insensitive (Snowflake default).
 */
export function filterAppliesToCard(def, cardId, cardColumns) {
  if (!def || !def.column) return false;
  if (Array.isArray(def.applies_to) && !def.applies_to.includes(cardId)) return false;
  if (!Array.isArray(cardColumns) || cardColumns.length === 0) return false;
  const want = String(def.column).toLowerCase();
  return cardColumns.some((c) => String(c).toLowerCase() === want);
}

/**
 * Wrap the given SQL with all applicable dashboard-level filters.
 *
 * @param {string}   sql            Original card SQL (already filter-rebuilt).
 * @param {object[]} definitions    All dashboard filter definitions.
 * @param {object}   values         Map of {filterId: [selectedValues]}.
 * @param {object}   ctx            { cardId, cardColumns } for auto-skip.
 * @returns {string} Possibly-wrapped SQL. Returns the input unchanged
 *                   when no filter applies.
 */
export function wrapSqlWithDashboardFilters(sql, definitions, values, ctx = {}) {
  if (!sql || !Array.isArray(definitions) || definitions.length === 0) return sql;
  const { cardId, cardColumns } = ctx;

  const predicates = [];
  for (const def of definitions) {
    if (!filterAppliesToCard(def, cardId, cardColumns)) continue;
    const vals = (values && values[def.id]) || def.default_values || [];
    const p = buildPredicate(def, vals);
    if (p) predicates.push(p);
  }
  if (predicates.length === 0) return sql;

  const inner = normalizeInner(sql);
  return `SELECT * FROM (\n${inner}\n) _df_wrap\nWHERE ${predicates.join(' AND ')}`;
}

/**
 * True iff at least one filter has selected/default values that would
 * produce a predicate. Useful for "active filter count" badges.
 */
export function hasActiveDashboardFilters(definitions, values) {
  if (!Array.isArray(definitions)) return false;
  return definitions.some((def) => {
    const vals = (values && values[def.id]) || def.default_values || [];
    if (!Array.isArray(vals)) return false;
    const op = (def?.operator || '').toUpperCase();
    if (op === 'IS NULL' || op === 'IS NOT NULL') {
      return vals.some((v) => v !== undefined && v !== '' && v !== false);
    }
    return vals.filter((v) => v !== undefined && v !== '').length > 0;
  });
}
